import { PageHero } from "@/components/PageHero";
import React, { useEffect, useState, useRef } from "react";
import { api, getSessionId } from "@/lib/api";
import { ClipboardList, Printer, Save, Plus, Trash2, User, ShieldAlert, Pill, Stethoscope, Building2, Phone, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
    demographics: {
        full_name: "", preferred_name: "", date_of_birth: "", sex: "",
        address: "", phone: "", primary_language: "",
        diagnosis: "", diagnosis_date: "", code_status: "",
    },
    insurance: {
        primary: "", member_id: "", group: "", phone: "",
        secondary: "", secondary_member_id: "",
    },
    pharmacy: { name: "", phone: "", address: "" },
    allergies: { medications: "", food: "", environmental: "" },
    medications: [],
    healthcare_team: [],
    emergency_contacts: [],
    notes: "",
};

const NEW_MED = { name: "", dose: "", frequency: "", purpose: "", prescriber: "" };
const NEW_DOC = { role: "", name: "", practice: "", phone: "", fax: "" };
const NEW_CONTACT = { relation: "", name: "", phone: "", notes: "" };

export default function PatientProfile() {
    const sid = getSessionId();
    const [data, setData] = useState(EMPTY);
    const [loaded, setLoaded] = useState(false);
    const saveTimer = useRef(null);

    useEffect(() => {
        let active = true;
        api.get(`/patient-profile/${sid}`).then(r => {
            if (!active) return;
            setData({
                ...EMPTY,
                ...r.data,
                demographics: { ...EMPTY.demographics, ...(r.data.demographics || {}) },
                insurance: { ...EMPTY.insurance, ...(r.data.insurance || {}) },
                pharmacy: { ...EMPTY.pharmacy, ...(r.data.pharmacy || {}) },
                allergies: { ...EMPTY.allergies, ...(r.data.allergies || {}) },
                medications: r.data.medications || [],
                healthcare_team: r.data.healthcare_team || [],
                emergency_contacts: r.data.emergency_contacts || [],
                notes: r.data.notes || "",
            });
        }).finally(() => { if (active) setLoaded(true); });
        return () => { active = false; };
    }, [sid]);

    const update = (path, value) => {
        setData(prev => {
            const next = { ...prev };
            if (typeof path === "string") {
                next[path] = value;
            } else {
                const [section, field] = path;
                next[section] = { ...next[section], [field]: value };
            }
            scheduleSave(next);
            return next;
        });
    };

    const updateList = (key, items) => {
        setData(prev => {
            const next = { ...prev, [key]: items };
            scheduleSave(next);
            return next;
        });
    };

    const scheduleSave = (payload) => {
        if (!loaded) return;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            api.put(`/patient-profile/${sid}`, payload).catch(() => { });
        }, 600);
    };

    const saveNow = async () => {
        try {
            await api.put(`/patient-profile/${sid}`, data);
            toast.success("Saved.");
        } catch {
            toast.error("Couldn't save. Try again?");
        }
    };

    const printSheet = () => window.print();

    // medication / provider / contact handlers
    const addRow = (key, template) => updateList(key, [...(data[key] || []), { ...template, _id: `r${Date.now()}${Math.random()}` }]);
    const updateRow = (key, idx, field, value) => {
        const arr = [...(data[key] || [])];
        arr[idx] = { ...arr[idx], [field]: value };
        updateList(key, arr);
    };
    const removeRow = (key, idx) => updateList(key, (data[key] || []).filter((_, i) => i !== idx));

    const d = data.demographics;
    const i = data.insurance;
    const p = data.pharmacy;
    const al = data.allergies;

    return (
        <div data-clarity-mask="true" className="space-y-10 animate-fade-in-up">
            <PageHero
                className="no-print"
                testId="patient-hero"
                kicker="Patient profile"
                kickerIcon={ClipboardList}
                title="Patient profile sheet"
                subtitle="One printable page for ER visits, new providers, and family members. Saves automatically."
                actions={<>
                    <button onClick={saveNow} data-testid="patient-save" className="ml-btn-secondary !min-h-[44px] !px-4">
                        <Save size={18} strokeWidth={1.6} /> Save
                    </button>
                    <button onClick={printSheet} data-testid="patient-print" className="ml-btn-primary !min-h-[44px] !px-4">
                        <Printer size={18} strokeWidth={1.6} /> Print sheet
                    </button>
                </>}
            />

            {/* Print header */}
            <div className="hidden print:block">
                <h1 className="text-3xl font-heading font-semibold">Patient Profile</h1>
                <p className="text-sm text-[#4C5551]">Printed from Memory Lane Care Navigator · {new Date().toLocaleDateString()}</p>
                <hr className="my-3" />
            </div>

            {/* Allergies — yellow CAUTION, top of page for visibility */}
            <section className="ml-card p-6 md:p-7 bg-yellow-soft !border-yellow-medium" data-testid="patient-allergies-section">
                <div className="flex items-center gap-3 mb-4">
                    <ShieldAlert strokeWidth={1.6} className="text-yellow-dark" />
                    <h2 className="text-2xl font-heading font-medium text-yellow-dark">Allergies & reactions</h2>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                    <Field label="Medications" testid="patient-allergies-meds"
                        value={al.medications} onChange={v => update(["allergies", "medications"], v)} multiline
                        placeholder="e.g. Penicillin (hives), sulfa drugs" />
                    <Field label="Food" testid="patient-allergies-food"
                        value={al.food} onChange={v => update(["allergies", "food"], v)} multiline
                        placeholder="e.g. Peanuts, shellfish" />
                    <Field label="Environmental" testid="patient-allergies-env"
                        value={al.environmental} onChange={v => update(["allergies", "environmental"], v)} multiline
                        placeholder="e.g. Latex, bee stings, pollen" />
                </div>
            </section>

            {/* Demographics — light teal */}
            <section className="ml-card p-6 md:p-7 bg-[#E6F4F4] !border-[#E6F4F4]" data-testid="patient-demographics-section">
                <div className="flex items-center gap-3 mb-4">
                    <User strokeWidth={1.6} className="text-teal" />
                    <h2 className="text-2xl font-heading font-medium text-teal">Patient information</h2>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Full legal name" testid="pt-name" value={d.full_name} onChange={v => update(["demographics", "full_name"], v)} />
                    <Field label="Preferred name" testid="pt-pref" value={d.preferred_name} onChange={v => update(["demographics", "preferred_name"], v)} />
                    <Field label="Date of birth" type="date" testid="pt-dob" value={d.date_of_birth} onChange={v => update(["demographics", "date_of_birth"], v)} />
                    <Field label="Sex" testid="pt-sex" value={d.sex} onChange={v => update(["demographics", "sex"], v)} placeholder="e.g. Female" />
                    <Field label="Phone" testid="pt-phone" value={d.phone} onChange={v => update(["demographics", "phone"], v)} placeholder="(555) 555-5555" />
                    <Field label="Primary language" testid="pt-lang" value={d.primary_language} onChange={v => update(["demographics", "primary_language"], v)} placeholder="e.g. English / Spanish" />
                    <Field label="Address" testid="pt-addr" value={d.address} onChange={v => update(["demographics", "address"], v)} multiline placeholder="Street, City, State, ZIP" />
                    <Field label="Diagnosis" testid="pt-dx" value={d.diagnosis} onChange={v => update(["demographics", "diagnosis"], v)} placeholder="e.g. Alzheimer's disease, moderate" />
                    <Field label="Diagnosis date" type="date" testid="pt-dx-date" value={d.diagnosis_date} onChange={v => update(["demographics", "diagnosis_date"], v)} />
                    <Field label="Code status" testid="pt-code" value={d.code_status} onChange={v => update(["demographics", "code_status"], v)} placeholder="e.g. Full code / DNR / DNI" />
                </div>
            </section>

            {/* Medications — cream */}
            <section className="ml-card p-6 md:p-7 bg-cream-dark !border-cream-dark" data-testid="patient-medications-section">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                        <Pill strokeWidth={1.6} className="text-[#7B5B1E]" />
                        <h2 className="text-2xl font-heading font-medium text-[#12776F]">Medications</h2>
                    </div>
                    <button onClick={() => addRow("medications", NEW_MED)} data-testid="med-add"
                        className="ml-btn-secondary !min-h-[40px] !py-1.5 !px-3 text-sm no-print">
                        <Plus size={16} strokeWidth={1.6} /> Add medication
                    </button>
                </div>
                {(data.medications || []).length === 0 ? (
                    <p className="text-[#4C5551] no-print">No medications yet. Tap &ldquo;Add medication&rdquo; to start.</p>
                ) : (
                    <div className="space-y-3">
                        {data.medications.map((m, idx) => (
                            <div key={m._id || idx} className="bg-white rounded-xl p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-start" data-testid={`med-row-${idx}`}>
                                <Compact testid={`med-name-${idx}`} label="Name" value={m.name} onChange={v => updateRow("medications", idx, "name", v)} className="md:col-span-2" placeholder="e.g. Donepezil" />
                                <Compact testid={`med-dose-${idx}`} label="Dose" value={m.dose} onChange={v => updateRow("medications", idx, "dose", v)} placeholder="10 mg" />
                                <Compact testid={`med-freq-${idx}`} label="Frequency" value={m.frequency} onChange={v => updateRow("medications", idx, "frequency", v)} placeholder="Once daily at bedtime" />
                                <Compact testid={`med-purpose-${idx}`} label="Purpose" value={m.purpose} onChange={v => updateRow("medications", idx, "purpose", v)} placeholder="Memory" />
                                <div className="flex items-end gap-2">
                                    <Compact testid={`med-prescriber-${idx}`} label="Prescriber" value={m.prescriber} onChange={v => updateRow("medications", idx, "prescriber", v)} placeholder="Dr. Lee" className="flex-1" />
                                    <button onClick={() => removeRow("medications", idx)} data-testid={`med-remove-${idx}`} className="text-coral-dark p-2 no-print">
                                        <Trash2 size={16} strokeWidth={1.6} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Healthcare team — blue */}
            <section className="ml-card p-6 md:p-7 bg-ml_blue-light !border-ml_blue-light" data-testid="patient-team-section">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                        <Stethoscope strokeWidth={1.6} className="text-ml_blue-dark" />
                        <h2 className="text-2xl font-heading font-medium text-ml_blue-dark">Healthcare team</h2>
                    </div>
                    <button onClick={() => addRow("healthcare_team", NEW_DOC)} data-testid="doc-add"
                        className="ml-btn-secondary !min-h-[40px] !py-1.5 !px-3 text-sm no-print">
                        <Plus size={16} strokeWidth={1.6} /> Add provider
                    </button>
                </div>
                {(data.healthcare_team || []).length === 0 ? (
                    <p className="text-[#4C5551] no-print">No providers added yet.</p>
                ) : (
                    <div className="space-y-3">
                        {data.healthcare_team.map((doc, idx) => (
                            <div key={doc._id || idx} className="bg-white rounded-xl p-4 grid grid-cols-1 md:grid-cols-6 gap-3 items-start" data-testid={`doc-row-${idx}`}>
                                <Compact testid={`doc-role-${idx}`} label="Role" value={doc.role} onChange={v => updateRow("healthcare_team", idx, "role", v)} placeholder="Primary care" />
                                <Compact testid={`doc-name-${idx}`} label="Name" value={doc.name} onChange={v => updateRow("healthcare_team", idx, "name", v)} className="md:col-span-2" placeholder="Dr. Smith" />
                                <Compact testid={`doc-practice-${idx}`} label="Practice" value={doc.practice} onChange={v => updateRow("healthcare_team", idx, "practice", v)} placeholder="Hill Family Med" />
                                <Compact testid={`doc-phone-${idx}`} label="Phone" value={doc.phone} onChange={v => updateRow("healthcare_team", idx, "phone", v)} placeholder="(555) 555-5555" />
                                <div className="flex items-end gap-2">
                                    <Compact testid={`doc-fax-${idx}`} label="Fax" value={doc.fax} onChange={v => updateRow("healthcare_team", idx, "fax", v)} className="flex-1" placeholder="optional" />
                                    <button onClick={() => removeRow("healthcare_team", idx)} data-testid={`doc-remove-${idx}`} className="text-coral-dark p-2 no-print">
                                        <Trash2 size={16} strokeWidth={1.6} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Pharmacy + Insurance side-by-side */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="ml-card p-6 md:p-7 bg-[#E6F4F4] !border-[#E6F4F4]" data-testid="patient-pharmacy-section">
                    <div className="flex items-center gap-3 mb-4">
                        <Building2 strokeWidth={1.6} className="text-teal" />
                        <h2 className="text-2xl font-heading font-medium text-teal">Pharmacy</h2>
                    </div>
                    <div className="space-y-3">
                        <Field label="Pharmacy name" testid="ph-name" value={p.name} onChange={v => update(["pharmacy", "name"], v)} placeholder="e.g. Walgreens #1234" />
                        <Field label="Phone" testid="ph-phone" value={p.phone} onChange={v => update(["pharmacy", "phone"], v)} placeholder="(555) 555-5555" />
                        <Field label="Address" testid="ph-addr" value={p.address} onChange={v => update(["pharmacy", "address"], v)} multiline />
                    </div>
                </div>
                <div className="ml-card p-6 md:p-7 bg-cream-dark !border-cream-dark" data-testid="patient-insurance-section">
                    <div className="flex items-center gap-3 mb-4">
                        <Phone strokeWidth={1.6} className="text-[#7B5B1E]" />
                        <h2 className="text-2xl font-heading font-medium text-[#12776F]">Insurance</h2>
                    </div>
                    <div className="space-y-3">
                        <Field label="Primary plan" testid="ins-primary" value={i.primary} onChange={v => update(["insurance", "primary"], v)} placeholder="e.g. Medicare + BCBS" />
                        <Field label="Member ID" testid="ins-member" value={i.member_id} onChange={v => update(["insurance", "member_id"], v)} />
                        <Field label="Group #" testid="ins-group" value={i.group} onChange={v => update(["insurance", "group"], v)} />
                        <Field label="Plan phone" testid="ins-phone" value={i.phone} onChange={v => update(["insurance", "phone"], v)} placeholder="On the back of the card" />
                        <Field label="Secondary plan (if any)" testid="ins-secondary" value={i.secondary} onChange={v => update(["insurance", "secondary"], v)} />
                        <Field label="Secondary member ID" testid="ins-secondary-id" value={i.secondary_member_id} onChange={v => update(["insurance", "secondary_member_id"], v)} />
                    </div>
                </div>
            </section>

            {/* Emergency contacts — emergency red ONLY thing red on this page */}
            <section className="ml-card p-6 md:p-7 bg-emergency-light !border-emergency-light" data-testid="patient-emergency-section">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                        <AlertTriangle strokeWidth={1.6} className="text-emergency-dark" />
                        <h2 className="text-2xl font-heading font-medium text-emergency-dark">Emergency contacts</h2>
                    </div>
                    <button onClick={() => addRow("emergency_contacts", NEW_CONTACT)} data-testid="contact-add"
                        className="ml-btn-secondary !min-h-[40px] !py-1.5 !px-3 text-sm no-print">
                        <Plus size={16} strokeWidth={1.6} /> Add contact
                    </button>
                </div>
                {(data.emergency_contacts || []).length === 0 ? (
                    <p className="text-[#4C5551] no-print">Add the people who should be called first.</p>
                ) : (
                    <div className="space-y-3">
                        {data.emergency_contacts.map((c, idx) => (
                            <div key={c._id || idx} className="bg-white rounded-xl p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-start" data-testid={`contact-row-${idx}`}>
                                <Compact testid={`contact-rel-${idx}`} label="Relation" value={c.relation} onChange={v => updateRow("emergency_contacts", idx, "relation", v)} placeholder="Daughter" />
                                <Compact testid={`contact-name-${idx}`} label="Name" value={c.name} onChange={v => updateRow("emergency_contacts", idx, "name", v)} className="md:col-span-2" />
                                <Compact testid={`contact-phone-${idx}`} label="Phone" value={c.phone} onChange={v => updateRow("emergency_contacts", idx, "phone", v)} placeholder="(555) 555-5555" />
                                <div className="flex items-end gap-2">
                                    <Compact testid={`contact-notes-${idx}`} label="Notes" value={c.notes} onChange={v => updateRow("emergency_contacts", idx, "notes", v)} className="flex-1" placeholder="e.g. POA" />
                                    <button onClick={() => removeRow("emergency_contacts", idx)} data-testid={`contact-remove-${idx}`} className="text-coral-dark p-2 no-print">
                                        <Trash2 size={16} strokeWidth={1.6} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Notes */}
            <section className="ml-card p-6 md:p-7" data-testid="patient-notes-section">
                <h2 className="text-xl font-heading font-medium text-teal mb-3">Other notes</h2>
                <textarea
                    data-testid="patient-notes"
                    value={data.notes}
                    onChange={e => update("notes", e.target.value)}
                    className="ml-input min-h-[120px] py-3"
                    placeholder="Mobility needs, communication preferences, dietary restrictions, comfort items, anything an ER provider should know in 30 seconds…"
                />
            </section>

            <TransitionPlans />
        </div>
    );
}

function TransitionPlans() {
    const sid = getSessionId();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        let active = true;
        const run = async () => {
            try {
                const r = await api.get(`/transition-plans/${sid}`);
                if (active) setItems(r.data.items || []);
            } catch { /* silent */ }
            finally { if (active) setLoading(false); }
        };
        run();
        return () => { active = false; };
    }, [sid, tick]);

    const remove = async (id) => {
        if (!window.confirm("Delete this plan?")) return;
        try {
            await api.delete(`/transition-plans/${id}`);
            toast.success("Deleted.");
            setTick((n) => n + 1);
        } catch {
            toast.error("Couldn't delete.");
        }
    };

    if (loading) return null;
    if (items.length === 0) {
        return (
            <section className="ml-card p-6" data-testid="patient-transition-plans-empty">
                <h2 className="text-2xl font-heading font-semibold tracking-tight">Saved transition plans</h2>
                <p className="mt-2 text-sm text-[#4C5551]">
                    None yet. When Lane drafts a day-of + first-week plan in chat, you&apos;ll see a
                    <strong> Save this plan </strong>
                    button — tap it and the plan lands here so you can come back to it on the actual day.
                </p>
            </section>
        );
    }

    return (
        <section className="ml-card p-6" data-testid="patient-transition-plans">
            <h2 className="text-2xl font-heading font-semibold tracking-tight">Saved transition plans</h2>
            <p className="mt-1 text-sm text-[#4C5551]">Tap a plan to read it. Use it on the day, edit it later if you want.</p>
            <ul className="mt-4 space-y-3">
                {items.map((p) => (
                    <li
                        key={p.id}
                        data-testid={`transition-plan-${p.id}`}
                        className="ml-card !border-cream-dark p-5 bg-cream-dark/30"
                    >
                        <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                            <div>
                                <h3 className="font-heading font-medium text-[#1F2624]">{p.title || "Transition plan"}</h3>
                                <p className="text-xs text-[#6E7A74] mt-0.5">
                                    {p.kind || "moving"} · saved {new Date(p.created_at).toLocaleDateString()}
                                </p>
                            </div>
                            <button
                                onClick={() => remove(p.id)}
                                data-testid={`transition-plan-delete-${p.id}`}
                                className="text-xs text-emergency-dark hover:underline"
                            >
                                Delete
                            </button>
                        </div>
                        {p.day_of && p.day_of.length > 0 && (
                            <div className="mt-2">
                                <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">Day-of</p>
                                <ul className="mt-1 space-y-1 text-sm text-[#1F2624]">
                                    {p.day_of.map((x, i) => <li key={i} className="flex gap-2"><span className="text-teal-dark">•</span><span>{x}</span></li>)}
                                </ul>
                            </div>
                        )}
                        {p.first_week && p.first_week.length > 0 && (
                            <div className="mt-3">
                                <p className="text-xs uppercase tracking-wider font-semibold text-stage_late-dark">First week</p>
                                <ul className="mt-1 space-y-1 text-sm text-[#1F2624]">
                                    {p.first_week.map((x, i) => <li key={i} className="flex gap-2"><span className="text-stage_late-dark">•</span><span>{x}</span></li>)}
                                </ul>
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </section>
    );
}

function Field({ label, testid, value, onChange, type = "text", multiline, placeholder }) {
    return (
        <div>
            <label className="ml-label">{label}</label>
            {multiline ? (
                <textarea data-testid={testid} value={value || ""} onChange={e => onChange(e.target.value)}
                    className="ml-input min-h-[68px] py-2.5" placeholder={placeholder} />
            ) : (
                <input data-testid={testid} type={type} value={value || ""} onChange={e => onChange(e.target.value)}
                    className="ml-input" placeholder={placeholder} />
            )}
        </div>
    );
}

function Compact({ label, testid, value, onChange, placeholder, className = "" }) {
    return (
        <div className={className}>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#4C5551] mb-1">{label}</label>
            <input data-testid={testid} value={value || ""} onChange={e => onChange(e.target.value)}
                className="w-full rounded-lg border border-[#E2E8F0] bg-white px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent"
                placeholder={placeholder} />
        </div>
    );
}
